<template>
  <div class="conditions">
    <el-card>
      <div slot="header" class="card-header">
        <span>{{ $t('autoPush.condTitle') }}</span>
        <div>
          <el-button type="primary" size="small" @click="handleAdd">{{ $t('autoPush.condAdd') }}</el-button>
          <el-button size="small" @click="$router.push('/auto-push')">{{ $t('autoPush.condBack') }}</el-button>
        </div>
      </div>

      <!-- 条件卡片列表 -->
      <el-row :gutter="20">
        <el-col :span="8" v-for="item in list" :key="item.id">
          <el-card shadow="hover" class="condition-card">
            <div class="condition-header">
              <h3>{{ item.name }}</h3>
              <el-tag :type="item.status === 'active' ? 'success' : 'info'" size="mini">
                {{ item.status === 'active' ? $t('autoPush.condEnabled') : $t('autoPush.condDisabled') }}
              </el-tag>
            </div>
            <p class="condition-desc">{{ item.description }}</p>
            <!-- 规则展示 -->
            <div class="condition-rules">
              <div v-for="(rule, idx) in (item.rules || [])" :key="idx" class="rule-item">
                <el-tag size="mini" type="info">{{ getFieldLabel(rule.field) }}</el-tag>
                <span class="rule-op">{{ getOpLabel(rule.operator) }}</span>
                <el-tag size="mini">{{ getValueLabel(rule.field, rule.value) }}</el-tag>
              </div>
              <div class="match-mode">{{ $t('autoPush.condMatchMode') }}{{ item.matchMode === 'AND' ? $t('autoPush.condMatchAnd') : $t('autoPush.condMatchOr') }}</div>
            </div>
            <!-- 关联内容已移除（2026-08-29）：条件只负责筛选人群，推送内容在任务里配置 -->
            <div class="condition-actions">
              <el-button type="text" size="small" @click="handleEdit(item)">{{ $t('autoPush.condEdit') }}</el-button>
              <el-button type="text" size="small" style="color: #F56C6C;" @click="handleDelete(item)">{{ $t('autoPush.condDelete') }}</el-button>
            </div>
          </el-card>
        </el-col>
      </el-row>
    </el-card>
  </div>
</template>

<script>
import { getConditions, deleteCondition } from '@/api/autoPush'
// 字段 / 操作符 / 枚举值 → 本地化标签（与条件编辑页共用同一份定义，避免两处硬编码不同步）
import { enumValueLabelKey, fieldLabelKey, operatorLabelKey } from './condition-fields'
import { BACK_TO_FRONT_OPERATOR } from './condition-form'

export default {
  name: 'AutoPushConditions',
  data() {
    return {
      list: [],
      loading: false
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    // 加载条件列表
    loadData() {
      this.loading = true
      getConditions().then(res => {
        this.list = res.data || []
      }).catch(() => {
        this.$message.error(this.$t('autoPush.condListLoadFail'))
      }).finally(() => {
        this.loading = false
      })
    },
    // 新增条件
    handleAdd() {
      this.$router.push('/auto-push/condition-edit')
    },
    // 编辑条件
    handleEdit(item) {
      this.$router.push('/auto-push/condition-edit/' + item.id)
    },
    // 字段标签：新 key 走共享定义（与编辑页下拉、任务页摘要同一份真源，新增字段不必改这里），
    // 历史数据里的旧 key 用下面这张表兜底 —— 缺一个列表就会直接显示英文原文（如 burdenLevel）
    getFieldLabel(field) {
      const sharedKey = fieldLabelKey(field)
      if (sharedKey) return this.$t('autoPush.' + sharedKey)

      const legacyMap = {
        stress: 'condFieldStress',
        self_care: 'condFieldSelfCare',
        burden: 'condFieldBurden',
        disease_stage: 'condFieldDisease',
        week: 'condFieldWeek',
        reg_days: 'condFieldRegDays',
        view_count: 'condFieldView',
        listen_duration: 'condFieldListen'
      }
      const legacyKey = legacyMap[field]
      return legacyKey ? this.$t('autoPush.' + legacyKey) : field
    },
    // 运算符标签：后端存标准操作符（= / >= / IN），编辑页提交别名（eq / contains），
    // 先统一成前端 key 再查共享表；否则列表里会直接显示 "IN" / "NOT IN" 这种英文符号
    getOpLabel(op) {
      const frontendOp = BACK_TO_FRONT_OPERATOR[op] || op
      const key = operatorLabelKey(frontendOp)
      if (key) return this.$t('autoPush.' + key)

      // 历史数据的蛇形别名不在共享表里，单独兜底
      const legacyMap = { not_contains: 'condOpNotContains' }
      const legacyKey = legacyMap[op]
      return legacyKey ? this.$t('autoPush.' + legacyKey) : op
    },
    // 值标签：枚举字段把数据库原始值（male / fair / high / en）翻译成本地化标签，
    // 多值（逗号分隔，配合 IN / NOT IN）逐个翻译后用 / 连接；
    // 非枚举字段（数字/次数）与未知值原样显示，不会丢失信息
    getValueLabel(field, rawValue) {
      const parts = Array.isArray(rawValue)
        ? rawValue
        : String(rawValue === null || rawValue === undefined ? '' : rawValue)
          .split(',')
          .map(s => s.trim())
          .filter(Boolean)
      if (!parts.length) return rawValue

      return parts.map(v => {
        const key = enumValueLabelKey(field, v)
        return key ? this.$t('autoPush.' + key) : v
      }).join(' / ')
    },
    // 删除条件
    async handleDelete(item) {
      try {
        await this.$confirm(this.$t('autoPush.condDeleteConfirm'), this.$t('common.tip'), {
          confirmButtonText: this.$t('common.confirm'),
          cancelButtonText: this.$t('common.cancel'),
          type: 'warning'
        })
        deleteCondition(item.id).then(() => {
          this.$message.success(this.$t('autoPush.condDeleteSuccess'))
          this.loadData()
        }).catch(() => {
          this.$message.error(this.$t('autoPush.condDeleteFail'))
        })
      } catch (e) {
        // 取消
      }
    }
  }
}
</script>

<style scoped>
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: bold;
  font-size: 16px;
}
.condition-card {
  margin-bottom: 20px;
}
.condition-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.condition-header h3 {
  margin: 0;
  font-size: 16px;
}
.condition-desc {
  color: #909399;
  font-size: 13px;
  margin: 10px 0;
}
.condition-rules {
  background: #f5f7fa;
  padding: 10px;
  border-radius: 4px;
  margin-bottom: 10px;
}
.rule-item {
  margin-bottom: 5px;
  display: flex;
  align-items: center;
  gap: 5px;
}
.rule-op {
  color: #E6A23C;
  font-weight: bold;
  font-size: 12px;
}
.match-mode {
  margin-top: 8px;
  font-size: 12px;
  color: #909399;
}
.condition-contents {
  margin-bottom: 10px;
}
.content-label {
  font-size: 12px;
  color: #909399;
}
.condition-actions {
  display: flex;
  justify-content: flex-end;
}
</style>
